import { useEffect, useState, useCallback } from "react";

// Close-on-outside-click, close-on-Escape, and return focus to whatever opened it.
//
// The first UI-behaviour hook in v2 — everything else in this folder fetches data. Kept
// deliberately small rather than general: it does the three things a popover must do and
// nothing else. A focus trap, roving arrow keys or collision flipping can grow out of
// this when something actually needs them.
//
// Escape is a document listener, matching how Modal in components/index.jsx does it.
// Outside-clicks listen for `mousedown` rather than `click`: a `click` fires only after
// mouseup, so dragging a text selection out of the panel and releasing outside would
// close it mid-gesture.
export function useDismissable(open, { ref, triggerRef, onClose }) {
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onDown = (e) => {
      // The trigger toggles itself. Without this exemption a press on the trigger would
      // close the panel here and reopen it on click, so it could never be dismissed by
      // pressing the thing that opened it.
      if (triggerRef?.current?.contains(e.target)) return;
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, ref, triggerRef, onClose]);

}

// Restores focus to the trigger, but only if focus is currently inside the panel.
//
// Leaving focus on a node that has just been removed drops the caret to the top of the
// document, which for someone driving by keyboard means starting the whole page again.
// The containment test is what stops it being rude: if the panel closed because the user
// clicked something else entirely, their focus is already where they want it.
//
// Called from the close handler rather than an effect, so it runs while the panel is
// still mounted and there is something to test containment against.
export function returnFocus(ref, triggerRef) {
  const active = document.activeElement;
  if (ref.current && active && ref.current.contains(active)) {
    triggerRef.current?.focus();
  }
}

// Tracks a trigger's position so a portalled popover can sit against it.
//
// Recomputed on resize and on scroll, and once more after `settleMs`. That delay is for
// the rail: .v2-shell animates grid-template-columns over .18s, so a rect measured the
// instant the panel opens is stale by the time the rail has finished moving.
export function useAnchorRect(open, triggerRef, settleMs = 220) {
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    const el = triggerRef.current;
    if (el) setRect(el.getBoundingClientRect());
  }, [triggerRef]);

  useEffect(() => {
    if (!open) { setRect(null); return undefined; }

    measure();
    const settle = setTimeout(measure, settleMs);
    window.addEventListener("resize", measure);
    // Capture phase, because the thing that scrolls is usually an inner container rather
    // than the window.
    window.addEventListener("scroll", measure, true);
    return () => {
      clearTimeout(settle);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, measure, settleMs]);

  return rect;
}
